// Monetisation core — provider-agnostic. The free converter stays fully local
// and open; "Pro" is a capability unlock via a license key from a payment
// provider (Lemon Squeezy / Paddle, chosen later). No custom auth/backend.
//
// Until a provider + checkout are wired, PAYWALL_ENABLED stays false so the free
// tool is never blocked; the Pro surface acts as a fake-door demand test
// (upgrade clicks are sent to analytics).

/** Free conversions allowed per day when the paywall is active. */
export const FREE_DAILY_LIMIT = 1;

/** Flip to true only once real checkout + license validation are live. */
export const PAYWALL_ENABLED = false;

/** Payment provider checkout URL. Empty until a provider is chosen. */
export const CHECKOUT_URL = "";

const PRO_KEY = "bs_pro";
const USAGE_KEY = "bs_usage";

export function isPro(): boolean {
  try {
    return localStorage.getItem(PRO_KEY) === "1";
  } catch {
    return false;
  }
}

export function setPro(on: boolean): void {
  try {
    if (on) localStorage.setItem(PRO_KEY, "1");
    else localStorage.removeItem(PRO_KEY);
  } catch {
    /* ignore */
  }
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function usedToday(): number {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (!raw) return 0;
    const u = JSON.parse(raw) as { date?: string; count?: number };
    return u.date === today() ? u.count ?? 0 : 0;
  } catch {
    return 0;
  }
}

/** Whether a conversion is allowed right now (paywall + daily free limit). */
export function canConvert(): boolean {
  if (!PAYWALL_ENABLED || isPro()) return true;
  return usedToday() < FREE_DAILY_LIMIT;
}

/** Count a completed free-tier conversion toward today's limit. */
export function recordConversion(): void {
  if (isPro() || !PAYWALL_ENABLED) return;
  try {
    localStorage.setItem(USAGE_KEY, JSON.stringify({ date: today(), count: usedToday() + 1 }));
  } catch {
    /* ignore */
  }
}

/**
 * Validate a license key against the payment provider. Single wiring point:
 * e.g. Lemon Squeezy → POST https://api.lemonsqueezy.com/v1/licenses/validate
 * with { license_key } (no secret needed) and check `valid`. No provider yet.
 */
export async function validateLicense(key: string): Promise<boolean> {
  void key;
  return false;
}

/** Send a custom event to GoatCounter (used for fake-door demand signals). */
export function trackEvent(name: string): void {
  try {
    (
      window as unknown as {
        goatcounter?: { count?: (o: { path: string; title: string; event: boolean }) => void };
      }
    ).goatcounter?.count?.({ path: name, title: name, event: true });
  } catch {
    /* ignore */
  }
}
